/* Generic config-driven CRUD page: table + search + create/edit dialog.
   Used by most admin pages to keep the codebase small and consistent. */
import { useMemo, useState } from "react";
import api, { errMsg, useList, mediaUrl } from "@/lib/api";
import { PageHeader, StatusBadge, EmptyState, Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function FieldInput({ field, values, setValue }) {
  const value = values[field.name] ?? "";
  const options = typeof field.options === "function" ? field.options(values) : field.options;
  const common = { id: `field-${field.name}`, "data-testid": `form-${field.name}` };
  if (field.type === "photo") {
    return <PhotoUpload field={field} value={value} values={values} setValue={setValue} />;
  }
  if (field.type === "select")
    return (
      <Select value={String(value)} onValueChange={(v) => setValue(field.name, v)}>
        <SelectTrigger {...common}>
          <SelectValue placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          {(options || []).map((o) => (
            <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  if (field.type === "textarea")
    return <Textarea {...common} value={value} rows={3} placeholder={field.placeholder}
      onChange={(e) => setValue(field.name, e.target.value)} />;
  if (field.type === "switch")
    return (
      <div className="pt-1">
        <Switch {...common} checked={!!values[field.name]} onCheckedChange={(v) => setValue(field.name, v)} />
      </div>
    );
  return (
    <Input
      {...common}
      type={field.type || "text"}
      step={field.step}
      value={value}
      placeholder={field.placeholder}
      onChange={(e) => setValue(field.name, e.target.value)}
    />
  );
}

function PhotoUpload({ field, value, values, setValue }) {
  const [busy, setBusy] = useState(false);
  const id = values.id;
  // uploadPath / deletePath: callbacks that receive `values` and return the endpoint path.
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!id) { toast.error("Save the record first, then upload a photo"); e.target.value = ""; return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5 MB"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const path = field.uploadPath(values);
      const r = await api.post(path, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      // API returns { photo_url } (or image_url). Pick whichever the caller expects.
      const returned = r.data[field.name] || r.data.photo_url || r.data.image_url;
      setValue(field.name, returned);
      toast.success("Photo uploaded");
    } catch (err) { toast.error(errMsg(err)); } finally {
      setBusy(false); e.target.value = "";
    }
  };
  const remove = async () => {
    if (!id) { setValue(field.name, null); return; }
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(field.deletePath(values));
      setValue(field.name, null);
      toast.success("Photo removed");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3"
         data-testid={`form-${field.name}-block`}>
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full border bg-muted">
        {value ? (
          <img src={mediaUrl(value)}
               alt={field.label}
               className="h-full w-full object-cover"
               data-testid={`form-${field.name}-preview`} />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">
            No photo
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-wrap gap-2">
        {!id && (
          <p className="w-full text-[11px] text-amber-700">
            Save this teacher first, then re-open Edit to upload a photo.
          </p>
        )}
        <label className={id ? "cursor-pointer" : "opacity-50"}>
          <input type="file" accept="image/*" className="hidden" onChange={upload}
                 disabled={busy || !id}
                 data-testid={`form-${field.name}-input`} />
          <span className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs hover:bg-muted/60">
            {busy ? "Uploading…" : (value ? "Replace photo" : "Upload photo")}
          </span>
        </label>
        {value && id && (
          <button type="button"
                  onClick={remove}
                  className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50"
                  data-testid={`form-${field.name}-remove`}>
            Remove
          </button>
        )}
        <p className="w-full text-[11px] text-muted-foreground">JPG/PNG/WebP, up to 5 MB. Square 400×400 works best.</p>
      </div>
    </div>
  );
}

export function FormDialog({ open, onOpenChange, title, fields, initial = {}, onSubmit, submitLabel = "Save" }) {
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  // re-seed when dialog opens with a different record
  const [seedKey, setSeedKey] = useState(null);
  if (open && seedKey !== (initial.__key || "new")) {
    setValues(initial);
    setSeedKey(initial.__key || "new");
  }
  if (!open && seedKey !== null) setSeedKey(null);

  const setValue = (name, v) => setValues((prev) => ({ ...prev, [name]: v }));

  const submit = async () => {
    for (const f of fields) {
      if (f.required && (values[f.name] === undefined || values[f.name] === "" || values[f.name] === null)) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setBusy(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (e) {
      if (!e?.__handled__) {
        toast.error(errMsg(e), { duration: 6000, style: { whiteSpace: "pre-line" } });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className={f.full !== false && (f.type === "textarea" || f.full) ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
              <Label htmlFor={`field-${f.name}`} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {f.label}{f.required && <span className="text-secondary"> *</span>}
              </Label>
              <FieldInput field={f} values={values} setValue={setValue} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="form-submit-button">
            {busy ? "Saving..." : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Convert form string values to a typed payload based on field defs
export function buildPayload(fields, values) {
  const out = {};
  for (const f of fields) {
    let v = values[f.name];
    if (v === "" || v === undefined) v = null;
    if (v !== null && f.type === "number") v = parseFloat(v);
    if (v !== null && f.int) v = parseInt(v, 10);
    out[f.name] = v;
  }
  return out;
}

export default function ResourcePage({
  title, description, endpoint, listEndpoint, columns, fields,
  canCreate = true, canEdit = true, canDelete = false,
  createLabel = "Add new", searchKeys = [], rowLink, transform,
  headerExtra, autoOpenCreate = false, testId, afterCreate, catchCreateError,
}) {
  const { data, loading, reload } = useList(listEndpoint || endpoint);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(autoOpenCreate);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter((r) => searchKeys.some((k) => String(r[k] || "").toLowerCase().includes(s)));
  }, [data, search, searchKeys]);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setDialogOpen(true); };

  const handleSubmit = async (values) => {
    let payload = buildPayload(fields, values);
    if (transform) payload = transform(payload, values);
    let created = null;
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else created = (await api.post(endpoint, payload)).data;
    } catch (e) {
      // Optional callback allows the parent to intercept a create-failure
      // (e.g. 409 duplicate) and render its own dialog. Returning truthy
      // suppresses the generic toast and re-throws so the FormDialog stays
      // open for the user to correct the input.
      if (!editing && catchCreateError && catchCreateError(e, values)) {
        e.__handled__ = true;
      }
      throw e;
    }
    toast.success(editing ? "Updated successfully" : "Created successfully");
    reload();
    if (!editing && created && afterCreate) afterCreate(created);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Are you sure? This cannot be undone.")) return;
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const initial = editing
    ? { ...Object.fromEntries(fields.map((f) => [f.name, editing[f.name] ?? ""])),
        id: editing.id, __key: editing.id }
    : Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? ""]));

  return (
    <div data-testid={testId || "resource-page"}>
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            {headerExtra}
            {canCreate && (
              <Button onClick={openCreate} data-testid="add-new-button">
                <Plus className="h-4 w-4 mr-1" /> {createLabel}
              </Button>
            )}
          </>
        }
      />
      {searchKeys.length > 0 && (
        <div className="relative max-w-sm mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="search-input" className="pl-9" placeholder="Search..."
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      )}
      <div className="rounded-lg border bg-card overflow-x-auto">
        {loading ? (
          <div className="p-12 flex justify-center"><Spinner /></div>
        ) : rows.length === 0 ? (
          <div className="p-6"><EmptyState /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {columns.map((c) => (
                  <TableHead key={c.key} className="text-xs font-bold uppercase tracking-wider">{c.label}</TableHead>
                ))}
                {(canEdit || canDelete) && <TableHead className="w-24" />}
              </TableRow>
            </TableHeader>
            <TableBody className="stagger-in">
              {rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-testid={`row-${row.id}`}
                  className={rowLink ? "cursor-pointer" : ""}
                  onClick={() => rowLink && navigate(rowLink(row))}
                >
                  {columns.map((c) => (
                    <TableCell key={c.key} className="text-sm">
                      {c.render ? c.render(row) : c.badge ? <StatusBadge status={row[c.key]} /> : row[c.key]}
                    </TableCell>
                  ))}
                  {(canEdit || canDelete) && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        {canEdit && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)}
                            data-testid={`edit-button-${row.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(row)}
                            data-testid={`delete-button-${row.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <FormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={editing ? `Edit ${title.replace(/s$/, "")}` : createLabel}
        fields={fields}
        initial={initial}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
